import { useState, useEffect } from 'react';
import FortuneCookie from './components/FortuneCookie';
import FortuneDisplay from './components/FortuneDisplay';
import ActionButtons from './components/ActionButtons';
import CustomFortuneModal from './components/CustomFortuneModal';
import ShareModal from './components/ShareModal';
import FortuneHistory from './components/FortuneHistory';

const defaultFortunes = [
  // 🥠 Hyper-Specific Fortunes
  "You will remember a password today without checking.",
  "At 3:17 PM, you will sigh for no reason.",
  "You will crave toast within the next 8 hours.",
  "You're going to say 'Huh… weird' out loud this week.",
  "You will find exactly one sock in the dryer.",
  "Someone will mispronounce your name in an email.",
  "You will check your phone for no reason at 2:43 PM.",
  "A song from 2009 will get stuck in your head tomorrow.",
  "You will drop something and catch it mid-air like a ninja.",
  "At 11:32 AM, you will yawn and make someone else yawn.",
  "You will walk into a room and forget why three times today.",
  "Someone will say 'you too' when it doesn't make sense.",
  "You will have a fake argument in your head and win.",
  "Your left eyelid will twitch at an inconvenient moment.",
  "You will see the number 37 exactly four times today.",
  "A random memory from 2014 will haunt you at midnight.",
  "You will accidentally like an old photo while stalking someone.",
  "Your stomach will growl during a quiet moment.",
  "You will spell a simple word wrong and question everything.",
  "Someone will ask 'what?' even though they heard you.",
  
  // 🤯 Brutally Mundane Truths
  "You should probably drink some water.",
  "You are thinking about something that already happened.",
  "You will open the fridge, forget why, and close it.",
  "You're doing your best, even if it looks chaotic.",
  "You will scroll past something important today.",
  "Your posture is terrible right now.",
  "You have 47 unread emails you'll never read.",
  "You're breathing manually now. You're welcome.",
  "You need to charge your phone soon.",
  "That thing you're procrastinating on is still waiting.",
  "You're wearing the same outfit as yesterday, mentally.",
  "Your screen time report will disappoint you this week.",
  "You've been sitting in the same position for too long.",
  "That notification isn't as important as you think.",
  "You're overthinking something that doesn't matter.",
  "Your to-do list is longer than your attention span.",
  "You will say 'I'm tired' at least five times today.",
  "You're going to bed later than you planned. Again.",
  "That coffee isn't helping anymore.",
  "You know what you should do. You won't do it.",
  
  // 😂 Sarcastic & Chaotic Ones
  "You will not rise to your full potential today, and that's fine.",
  "Your vibe is strong but confusing.",
  "The universe is looking away right now. Do whatever.",
  "You will regret nothing except that one thing.",
  "Your energy is 'chaotic neutral' today.",
  "You're about to make a decision. It will be fine. Probably.",
  "The stars have aligned. They're laughing at you.",
  "Your aura is the color of a loading screen.",
  "Congratulations! You're the main character of a sitcom nobody's watching.",
  "Your life is a group project and you're doing all the work.",
  "The universe said 'lol' and moved on.",
  "You're one minor inconvenience away from a villain arc.",
  "Your guardian angel just clocked out for the day.",
  "Mercury is in Gatorade. That explains everything.",
  "You're not lost, you're on an unplanned adventure.",
  "Your parallel universe self is doing way better.",
  "The simulation is buffering. Please wait.",
  "You're the reason the instructions say 'do not eat'.",
  "Your life's soundtrack is elevator music right now.",
  "Chaos chose you. You didn't choose chaos.",
  
  // 🌈 Weirdly Comforting Fortunes
  "Someone, somewhere, is proud of you and you don't even know it.",
  "You have survived 100% of your days so far.",
  "You are doing better than you think.",
  "The future you is softly rooting for you.",
  "You are someone's reason to smile today.",
  "You're exactly where you need to be right now.",
  "Your existence makes the universe more interesting.",
  "You're allowed to be proud of small victories.",
  "Someone remembers a kind thing you did years ago.",
  "You're more loved than you realize.",
  "Your younger self would be proud of you.",
  "You've made it through harder days than this.",
  "Someone thinks of you when they hear your favorite song.",
  "You're doing an amazing job at being yourself.",
  "The world is better with you in it.",
  "You're someone's comfort person and don't even know it.",
  "Your smile has made someone's day before.",
  "You're growing even when you don't feel it.",
  "Someone is grateful you exist.",
  "You're exactly the right amount of everything.",
  
  // 🐦 Absurd Nonsense Fortunes
  "A goose will think of you.",
  "You will win an argument in the shower later.",
  "The moon is aware of you.",
  "You will forget this fortune almost immediately.",
  "A pigeon has chosen you as its favorite human.",
  "You are the main character in a squirrel's story.",
  "The void has noticed you. It approves.",
  "You will sneeze exactly three times this week.",
  "A cloud shaped like a potato is thinking about you.",
  "You are cosmically significant to at least one houseplant.",
  "A cat in another dimension just meowed your name.",
  "You will witness a bird doing something suspicious.",
  "The number 7 has a crush on you.",
  "A parallel universe version of you just tripped.",
  "You are being haunted by a friendly ghost who's bad at haunting.",
  "A raccoon respects your life choices.",
  "You will see a dog and think 'that's a good dog'.",
  "The universe blinked and missed what you just did.",
  "A butterfly in Brazil just caused your next minor inconvenience.",
  "You are the chosen one. For what? Nobody knows.",
  "A potato in Idaho is spiritually connected to you.",
  "You will make eye contact with a bird. It will be awkward.",
  "The cosmic vending machine is out of your favorite destiny.",
  "A frog somewhere just croaked in your honor.",
  "You are being watched by a very confused alien.",
  "The matrix glitched but nobody noticed except you.",
  "A sandwich is thinking about you right now.",
  "You will witness something fall and say 'I'm not picking that up'.",
  "The universe tried to send you a sign but it got lost in the mail.",
  "You are the reason a random NPC exists in someone's dream."
];

const chaosFortunes = [
  "Your furniture is judging you.",
  "Your aura is 65% snack energy.",
  "The plants in your room are gossiping about you.",
  "Your sleep paralysis demon thinks you're boring.",
  "You're radiating 'I forgot to reply to that text' energy.",
  "Your FBI agent is concerned about your search history.",
  "The universe is buffering your good luck.",
  "Your inner child is filing a complaint.",
  "You're one bad day away from becoming a cryptid.",
  "Your vibe is 'unhinged but make it fashion'.",
  "The simulation forgot to render your motivation today.",
  "Your comfort zone called. It wants a restraining order.",
  "You're the reason aliens won't visit Earth.",
  "Your brain has 47 tabs open and all of them are frozen.",
  "The void stared back and said 'yikes'.",
  "Your life's loading screen is stuck at 67%.",
  "You're giving 'main character having a breakdown' energy.",
  "Your guardian angel requested a transfer.",
  "The cosmic forces are playing rock-paper-scissors with your fate.",
  "Your energy is 'chaotic goblin at 3 AM'.",
  "The universe hit 'shuffle' on your personality today.",
  "Your aura looks like a Windows error message.",
  "You're one minor inconvenience away from feral.",
  "The timeline split and you got the weird one.",
  "Your vibe is 'sentient laundry pile'.",
  "The matrix tried to delete you but the file was corrupted.",
  "Your life's soundtrack is dial-up internet sounds.",
  "You're the plot twist nobody asked for.",
  "The cosmic algorithm thinks you're a bot.",
  "Your energy is 'raccoon in a dumpster at midnight'."
];

const zenFortunes = [
  "You are exactly where you need to be right now.",
  "Your presence brings light to those around you.",
  "You are growing in ways you cannot yet see.",
  "The universe is conspiring in your favor.",
  "You are worthy of all the good things coming your way.",
  "Your journey is unfolding perfectly.",
  "You are stronger than you realize.",
  "Peace is already within you.",
  "You are loved beyond measure.",
  "Your kindness creates ripples of positive change.",
  "You are enough, just as you are.",
  "The best version of you is emerging every day.",
  "You are a beacon of hope for someone.",
  "Your heart knows the way forward.",
  "You are deserving of rest and gentleness.",
  "The universe celebrates your existence.",
  "You are making a difference, even in small ways.",
  "Your soul is beautiful and radiant.",
  "You are surrounded by infinite possibilities.",
  "Your inner wisdom guides you perfectly.",
  "You are a masterpiece in progress.",
  "The world is better because you're in it.",
  "You are held by the love of the universe.",
  "Your light shines brightest when you're being yourself.",
  "You are on the path meant for you.",
  "Your presence is a gift to this world.",
  "You are blooming at your own perfect pace.",
  "The universe whispers encouragement to your soul.",
  "You are a miracle experiencing itself.",
  "Your journey is sacred and meaningful."
];

type FortuneMode = 'normal' | 'chaos' | 'zen';

export default function Home() {
  const [fortunes, setFortunes] = useState<string[]>(defaultFortunes);
  const [currentFortune, setCurrentFortune] = useState<string>('');
  const [isCracked, setIsCracked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [key, setKey] = useState(0);
  const [fortuneHistory, setFortuneHistory] = useState<string[]>([]);
  const [fortuneMode, setFortuneMode] = useState<FortuneMode>('normal');
  const [showHistory, setShowHistory] = useState(false);

  // Load history from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('fortuneHistory');
    if (saved) {
      try {
        setFortuneHistory(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load fortune history');
      }
    }
  }, []);

  const crackCookie = () => {
    let fortunePool = fortunes;
    if (fortuneMode === 'chaos') {
      fortunePool = chaosFortunes;
    } else if (fortuneMode === 'zen') {
      fortunePool = zenFortunes;
    }
    
    const randomFortune = fortunePool[Math.floor(Math.random() * fortunePool.length)];
    setCurrentFortune(randomFortune);
    setIsCracked(true);
    
    // Add to history (keep last 5)
    const newHistory = [randomFortune, ...fortuneHistory].slice(0, 5);
    setFortuneHistory(newHistory);
    localStorage.setItem('fortuneHistory', JSON.stringify(newHistory));
  };

  const getAnotherFortune = () => {
    setIsCracked(false);
    setCurrentFortune('');
    setKey(prev => prev + 1);
  };

  const addCustomFortune = (fortune: string) => {
    if (!fortune || fortune.trim() === '') {
      console.error('Empty fortune provided');
      return;
    }
    setFortunes(prev => [...prev, fortune.trim()]);
    setShowModal(false);
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const toggleMode = (mode: FortuneMode) => {
    setFortuneMode(mode);
    setIsCracked(false);
    setCurrentFortune('');
    setKey(prev => prev + 1);
  };

  const toggleHistory = () => {
    setShowHistory(prev => !prev);
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 sm:p-8 relative overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 via-pink-900/20 to-cyan-900/30 animate-gradientShift"></div>
      
      {/* Retro grid floor */}
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: `
          linear-gradient(to right, #ff00ff 1px, transparent 1px),
          linear-gradient(to bottom, #ff00ff 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px',
        transform: 'perspective(500px) rotateX(60deg)',
        transformOrigin: 'center bottom'
      }}></div>

      {/* Drifting stars */}
      <div className="absolute inset-0">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-drift"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${15 + Math.random() * 10}s`,
              opacity: 0.3 + Math.random() * 0.7
            }}
          ></div>
        ))}
      </div>

      {/* Glowing orbs */}
      <div className="absolute top-20 left-20 w-64 h-64 bg-purple-500 rounded-full blur-3xl opacity-20 animate-pulse"></div>
      <div className="absolute bottom-20 right-20 w-80 h-80 bg-cyan-500 rounded-full blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-pink-500 rounded-full blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>

      <div className="relative z-10 w-full max-w-4xl">
        {/* Main Fortune Area */}
        <div className="w-full">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-2 sm:mb-4 neon-text animate-neonPulse px-2" style={{ fontFamily: '"Press Start 2P", monospace, "Pacifico", serif' }}>
            🔮 NEON FORTUNE
          </h1>
          <p className="text-center text-cyan-300 mb-4 sm:mb-8 text-sm sm:text-lg md:text-xl tracking-wider neon-text-subtle px-2">
            CLICK TO REVEAL YOUR FORTUNE
          </p>

          {/* Mode Selector */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-4 sm:mb-8 px-2">
            <button
              onClick={() => toggleMode('normal')}
              className={`px-3 sm:px-6 py-2 sm:py-3 border-2 sm:border-4 font-bold tracking-wider uppercase transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer ${
                fortuneMode === 'normal' 
                  ? 'bg-pink-400/20 border-pink-400 text-pink-400 neon-button-active' 
                  : 'bg-black border-gray-500 text-gray-400 hover:border-gray-300'
              }`}
              style={{ fontFamily: '"Press Start 2P", monospace' }}
            >
              <span className="text-[8px] sm:text-xs">Normal</span>
            </button>
            <button
              onClick={() => toggleMode('chaos')}
              className={`px-3 sm:px-6 py-2 sm:py-3 border-2 sm:border-4 font-bold tracking-wider uppercase transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer ${
                fortuneMode === 'chaos' 
                  ? 'bg-cyan-400/20 border-cyan-400 text-cyan-400 neon-button-active' 
                  : 'bg-black border-gray-500 text-gray-400 hover:border-gray-300'
              }`}
              style={{ fontFamily: '"Press Start 2P", monospace' }}
            >
              <span className="text-[8px] sm:text-xs">Chaos Mode</span>
            </button>
            <button
              onClick={() => toggleMode('zen')}
              className={`px-3 sm:px-6 py-2 sm:py-3 border-2 sm:border-4 font-bold tracking-wider uppercase transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer ${
                fortuneMode === 'zen' 
                  ? 'bg-purple-400/20 border-purple-400 text-purple-400 neon-button-active' 
                  : 'bg-black border-gray-500 text-gray-400 hover:border-gray-300'
              }`}
              style={{ fontFamily: '"Press Start 2P", monospace' }}
            >
              <span className="text-[8px] sm:text-xs">Zen Mode</span>
            </button>
          </div>

          <FortuneCookie 
            key={key}
            isCracked={isCracked} 
            onCrack={crackCookie} 
          />

          <FortuneDisplay 
            fortune={currentFortune} 
            isVisible={isCracked} 
          />

          <ActionButtons 
            onAnotherFortune={getAnotherFortune}
            onMakeYourOwn={() => setShowModal(true)}
            onShare={handleShare}
            onViewHistory={toggleHistory}
            showButtons={isCracked}
          />
        </div>

        {/* History Below - Only show if there's history and user clicked to view */}
        {fortuneHistory.length > 0 && showHistory && (
          <div className="mt-8 sm:mt-12 w-full">
            <FortuneHistory history={fortuneHistory} />
          </div>
        )}
      </div>

      {showModal && (
        <CustomFortuneModal 
          onClose={() => setShowModal(false)}
          onSubmit={addCustomFortune}
        />
      )}

      {showShareModal && (
        <ShareModal 
          onClose={() => setShowShareModal(false)}
          fortune={currentFortune}
        />
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap');
        
        @keyframes gradientShift {
          0%, 100% { 
            background: linear-gradient(135deg, rgba(139, 0, 139, 0.3), rgba(255, 0, 255, 0.2), rgba(0, 255, 255, 0.3));
          }
          33% { 
            background: linear-gradient(135deg, rgba(255, 0, 255, 0.3), rgba(0, 255, 255, 0.2), rgba(139, 0, 139, 0.3));
          }
          66% { 
            background: linear-gradient(135deg, rgba(0, 255, 255, 0.3), rgba(139, 0, 139, 0.2), rgba(255, 0, 255, 0.3));
          }
        }

        @keyframes drift {
          0% {
            transform: translate(0, 0);
          }
          100% {
            transform: translate(100px, 100px);
          }
        }

        @keyframes neonPulse {
          0%, 100% {
            text-shadow: 
              0 0 10px #ff00ff,
              0 0 20px #ff00ff,
              0 0 30px #ff00ff,
              0 0 40px #ff00ff;
          }
          50% {
            text-shadow: 
              0 0 5px #ff00ff,
              0 0 10px #ff00ff,
              0 0 15px #ff00ff,
              0 0 20px #ff00ff;
          }
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slideDown {
          animation: slideDown 0.4s ease-out;
        }

        .neon-text {
          color: #fff;
          text-shadow: 
            0 0 10px #ff00ff,
            0 0 20px #ff00ff,
            0 0 30px #ff00ff,
            0 0 40px #ff00ff,
            0 0 70px #ff00ff;
        }

        .neon-text-subtle {
          text-shadow: 
            0 0 5px #00ffff,
            0 0 10px #00ffff,
            0 0 15px #00ffff;
        }

        .neon-button-active {
          box-shadow: 
            0 0 20px currentColor,
            0 0 40px currentColor,
            inset 0 0 20px currentColor;
        }

        .neon-button-purple {
          box-shadow: 
            0 0 20px rgba(168, 85, 247, 0.6),
            0 0 40px rgba(168, 85, 247, 0.4),
            inset 0 0 20px rgba(168, 85, 247, 0.2);
        }

        .neon-button-purple:hover {
          box-shadow: 
            0 0 30px rgba(168, 85, 247, 0.8),
            0 0 60px rgba(168, 85, 247, 0.6),
            inset 0 0 30px rgba(168, 85, 247, 0.3);
        }

        .animate-gradientShift {
          animation: gradientShift 10s ease infinite;
        }

        .animate-drift {
          animation: drift linear infinite;
        }

        .animate-neonPulse {
          animation: neonPulse 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
